import { useState, useMemo, useEffect, useRef } from "preact/hooks";
import createUtilityCanvas from "@brickme/project-core/src/render/create-utility-canvas.ts";
import type {
	GenericSize,
	GenericPosition,
} from "@brickme/project-core/src/model/geom.ts";
import { multiplySize } from "@brickme/project-core/src/model/geom.ts";
import type { Bitmap } from "@brickme/project-core/src/bitmap/types.ts";
import { ensureExhaustive } from "@brickme/project-core/src/utils/never.ts";
import { useCurrentBrickedBuild } from "../picture-build/picture-build-context.tsx";
import useMeasure from "../hooks/use-measure.ts";
import createRenderer from "./create-renderer.ts";
import type {
	LoadedScene,
	RenderRunner,
	RenderSource,
} from "./render-runner-type.ts";
import { usePicture } from "../picture-edit/picture-edit-context.tsx";
import { useRenderMode } from "./render-mode-context.tsx";
import { createDrawSize } from "./canvas-bounds.ts";
import type { FaceBoundingBox } from "@brickme/project-core/src/model/project.ts";

type Options = {
	readonly sourceImage: Bitmap;
	readonly faces:
		| readonly Pick<FaceBoundingBox, "x" | "y" | "width" | "height">[]
		| undefined;
	readonly drawBaseplates?: boolean;
	readonly drawDebugFaces?: boolean;
	readonly loadedScene?: LoadedScene;
	readonly minTotalMargin?: number;
	readonly renderScale?: number;
	// Opt-in: render at this offset instead of picture.imageZoomOffset, so a live
	// pan can drive the canvas without committing to the picture context on every
	// move. Undefined = use the committed offset (default for all other consumers).
	readonly imageZoomOffsetOverride?: GenericPosition<number>;
};

function useCanvasRenderer({
	sourceImage,
	faces,
	drawBaseplates,
	drawDebugFaces,
	loadedScene,
	minTotalMargin = 20,
	renderScale = 1,
	imageZoomOffsetOverride,
}: Options) {
	const bricked = useCurrentBrickedBuild();

	// Render worker
	const [renderer] = useState<RenderRunner<HTMLCanvasElement>>(createRenderer);
	useEffect(() => {
		return () => {
			renderer.terminate();
		};
	}, [renderer]);

	// Canvas
	const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
	const [setCanvasParentRef, parentSize] = useMeasure<HTMLElement>();
	const { width: parentWidth, height: parentHeight } = parentSize;
	const brickedImage = bricked?.image;
	useEffect(() => {
		if (canvas === null) {
			return;
		}

		renderer.setCanvas(canvas);
	}, [canvas, renderer]);
	useEffect(() => {
		renderer.setSourceImage(sourceImage, document.createElement("canvas"));
	}, [sourceImage, renderer]);
	useEffect(() => {
		renderer.setDrawBaseplates(!!drawBaseplates);
	}, [drawBaseplates, renderer]);
	useEffect(() => {
		renderer.setDrawDebugFaces(!!drawDebugFaces);
	}, [drawDebugFaces, renderer]);
	useEffect(() => {
		renderer.setFaces(faces);
	}, [faces, renderer]);

	// Scene
	useEffect(() => {
		if (renderer) {
			renderer.setLoadedScene(loadedScene);
		}
	}, [renderer, loadedScene]);

	// Perform render
	const { picture } = usePicture();
	const { activeRenderMode } = useRenderMode();
	const renderSource = useMemo((): RenderSource => {
		if (brickedImage === undefined || activeRenderMode === "source") {
			return {
				type: "source",
			};
		}
		if (activeRenderMode === "bricked" || activeRenderMode === "solid") {
			return {
				type: activeRenderMode,
				build: brickedImage,
			};
		}
		ensureExhaustive(activeRenderMode);
	}, [activeRenderMode, brickedImage]);
	useEffect(() => {
		renderer.setSource(renderSource);
	}, [renderer, renderSource]);

	// Should be a quicker way to do this. Only want a new utility canvas when renderSource
	// leads to dimension changes
	const { buildSize, drawSize } = useMemo(() => {
		const buildSize = multiplySize(
			picture.numberOfBasePlates,
			picture.basePlateSize,
		);
		const drawSize = createDrawSize(
			buildSize,
			{
				width: parentWidth,
				height: parentHeight,
			},
			loadedScene?.scene,
			{ minTotalMargin },
		);
		return { drawSize, buildSize };
	}, [picture, loadedScene, parentWidth, parentHeight, minTotalMargin]);

	// Backing-store size = layout size * supersample. Rounded to integers so the
	// canvas dimensions and brick sprites land on whole pixels
	const renderDrawSize = useMemo(
		() => ({
			width: Math.round(drawSize.width * renderScale),
			height: Math.round(drawSize.height * renderScale),
		}),
		[drawSize, renderScale],
	);
	const sentUtilitySizes = useRef<{
		renderDrawSize: GenericSize<number>;
		buildSize: GenericSize<number>;
	}>();
	useEffect(() => {
		const { current: currentSentUtilitySizes } = sentUtilitySizes;
		if (
			currentSentUtilitySizes === undefined ||
			currentSentUtilitySizes.buildSize.width !== buildSize.width ||
			currentSentUtilitySizes.buildSize.height !== buildSize.height ||
			currentSentUtilitySizes.renderDrawSize.width !== renderDrawSize.width ||
			currentSentUtilitySizes.renderDrawSize.height !== renderDrawSize.height
		) {
			const utilityCanvas = createUtilityCanvas(
				{ buildMap: buildSize, drawBounds: renderDrawSize },
				(width, height) => {
					const canvas = document.createElement("canvas");
					canvas.width = width;
					canvas.height = height;
					return canvas;
				},
			);
			renderer.setUtilityCanvas(utilityCanvas, renderDrawSize);
			sentUtilitySizes.current = {
				renderDrawSize,
				buildSize,
			};
		}
	}, [renderer, buildSize, renderDrawSize]);

	// Important this happens last, never want picture config to go in before new utility canvas
	const renderPicture = useMemo(
		() =>
			imageZoomOffsetOverride === undefined
				? picture
				: { ...picture, imageZoomOffset: imageZoomOffsetOverride },
		[picture, imageZoomOffsetOverride],
	);
	useEffect(() => {
		renderer.setPictureConfiguration(renderPicture);
	}, [renderPicture, renderer]);

	return {
		canvasRef: setCanvas,
		canvasParentRef: setCanvasParentRef,
		drawSize,
		renderDrawSize,
		parentSize,
	};
}

export type { LoadedScene };
export default useCanvasRenderer;
